import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { fetchOrders, fetchOrderById, deleteOrderApi } from '../../utils/orderApi';
import { getOrderId, formatOrderMoney, formatOrderDate } from './orderUtils';
import OrderCreate from './OrderCreate';
import OrderEdit from './OrderEdit';
import '../css/Category.css';

const OrderPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState(null);

  const [filterText, setFilterText] = useState('');
  const [sortKey, setSortKey] = useState('OrderedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [PAGE_SIZE, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const showToast = (message, type = 'danger') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchOrderList = async (currentPage, pageSize, search, sk, sd) => {
    setLoading(true);
    setError(null);
    try {
      const { items, totalRecords } = await fetchOrders({
        pageIndex: currentPage,
        pageSize,
        search,
        sortColumn: sk,
        sortBy: sd.toUpperCase(),
      });
      setOrders(items);
      setTotalCount(totalRecords);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderList(page, PAGE_SIZE, filterText, sortKey, sortDir);
  }, [page, PAGE_SIZE, filterText, sortKey, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = async (row) => {
    const id = getOrderId(row);
    try {
      const data = await fetchOrderById(id);
      setEditItem(data ?? row);
    } catch {
      setEditItem(row);
    }
    setShowEditModal(true);
  };

  const handleDelete = async (row) => {
    const id = getOrderId(row);
    if (!window.confirm(`Delete order #${id}?`)) return;
    try {
      await deleteOrderApi(id);
      showToast('Order deleted successfully.', 'success');
      fetchOrderList(page, PAGE_SIZE, filterText, sortKey, sortDir);
    } catch (e) {
      showToast(e.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const goToPage = (p) => setPage(Math.max(1, Math.min(p, totalPages)));
  const handleFilter = (val) => { setFilterText(val); setPage(1); };
  const handlePageSize = (val) => { setPageSize(Number(val)); setPage(1); };

  const handleSortCol = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <i className="mdi mdi-sort ms-1" style={{ opacity: 0.3 }}></i>;
    return sortDir === 'asc'
      ? <i className="mdi mdi-sort-ascending ms-1" style={{ color: '#6f42c1' }}></i>
      : <i className="mdi mdi-sort-descending ms-1" style={{ color: '#6f42c1' }}></i>;
  };

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Orders</span>
      </p>

      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              {toast && (
                <div className={`alert alert-${toast.type} py-2 mb-3`}>{toast.message}</div>
              )}

              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h4 className="card-title mb-0">Orders</h4>
                <div className="d-flex align-items-center gap-2">
                  <div className="cat-filter-wrap">
                    <i className="mdi mdi-magnify cat-filter-icon"></i>
                    <input
                      type="text"
                      className="cat-filter-input"
                      placeholder="Search orders..."
                      value={filterText}
                      onChange={(e) => handleFilter(e.target.value)}
                    />
                  </div>
                  <button type="button" className="cat-add-btn" onClick={() => setShowAddModal(true)}>
                    <i className="mdi mdi-plus" style={{ marginRight: 6 }}></i> Add Order
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped mb-0">
                    <thead>
                      <tr>
                        <th className="cat-sort-th" onClick={() => handleSortCol('OrderId')}>ID <SortIcon col="OrderId" /></th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('OrderNumber')}>Order # <SortIcon col="OrderNumber" /></th>
                        <th>User</th>
                        <th>Status</th>
                        <th>Payment</th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('TotalAmount')}>Total <SortIcon col="TotalAmount" /></th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('OrderedAt')}>Ordered At <SortIcon col="OrderedAt" /></th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-4 text-muted">No orders found.</td></tr>
                      ) : orders.map((row) => {
                        const id = getOrderId(row);
                        return (
                          <tr key={id}>
                            <td>{id}</td>
                            <td>{row.OrderNumber ?? row.orderNumber ?? '—'}</td>
                            <td>{row.UserId ?? row.userId}</td>
                            <td>{row.OrderStatus ?? row.orderStatus}</td>
                            <td>{row.PaymentStatus ?? row.paymentStatus}</td>
                            <td>{formatOrderMoney(row.TotalAmount ?? row.totalAmount)}</td>
                            <td>{formatOrderDate(row.OrderedAt ?? row.orderedAt)}</td>
                            <td>
                              <button type="button" className="btn btn-sm btn-outline-info me-1" style={{ border: 'none' }} onClick={() => navigate(`/admin/order/view/${id}`)} title="View">
                                <i className="mdi mdi-eye"></i>
                              </button>
                              <button type="button" className="btn btn-sm btn-outline-primary me-1" style={{ border: 'none' }} onClick={() => openEdit(row)} title="Edit">
                                <i className="mdi mdi-pencil"></i>
                              </button>
                              <button type="button" className="btn btn-sm btn-outline-danger" style={{ border: 'none' }} onClick={() => handleDelete(row)} title="Delete">
                                <i className="mdi mdi-delete"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && !error && totalCount > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="cat-page-info">Rows per page:</span>
                    <select className="cat-pagesize-select" value={PAGE_SIZE} onChange={(e) => handlePageSize(e.target.value)}>
                      <option value={10}>10</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="cat-page-info">
                      Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, totalCount)} of {totalCount}
                    </span>
                  </div>
                  <div className="cat-pagination">
                    <button type="button" className="cat-page-btn" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1}>
                      <i className="mdi mdi-chevron-left"></i>
                    </button>
                    <button type="button" className="cat-page-btn" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages}>
                      <i className="mdi mdi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <OrderCreate
          onClose={() => setShowAddModal(false)}
          onSuccess={(msg) => {
            setShowAddModal(false);
            showToast(msg, 'success');
            fetchOrderList(page, PAGE_SIZE, filterText, sortKey, sortDir);
          }}
        />
      )}

      {showEditModal && editItem && (
        <OrderEdit
          editItem={editItem}
          onClose={() => { setShowEditModal(false); setEditItem(null); }}
          onSuccess={(msg) => {
            setShowEditModal(false);
            setEditItem(null);
            showToast(msg, 'success');
            fetchOrderList(page, PAGE_SIZE, filterText, sortKey, sortDir);
          }}
        />
      )}
    </AdminLayout>
  );
};

export default OrderPage;
